import React, { useEffect, useState, useRef } from "react";
import scrollTo from "gatsby-plugin-smoothscroll";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import RichText from "@qlik/qlik/src/components/RichText";

import MobileTabsScrollingSection from "./MobileTabsScrollingSection";
import DesktopTabsScrollingSection from "./DesktopTabsScrollingSection";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

const TabsScrollingSection = ({ data }) => {
    const { title, subtitle, body, cardItems = [], backgroundColor, additionalConfiguration, customId } = data ?? {};
    const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 768);
    const [activeSection, setActiveSection] = useState(btoa(cardItems?.[0]?.id || "").slice(0, 8));
    const sectionRefs = useRef({});

    // Get the class name for the section padding
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration);
    const { sectionColorMapping } = getColorClassName(backgroundColor);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(typeof window !== "undefined" && window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        const options = {
            root: null,
            rootMargin: "0px",
            threshold: 1,
        };
        const handleIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const id = entry.target.getAttribute("id");

                    const refCurrent = Object.values(sectionRefs.current).find((ref) => {
                        return ref.id === id;
                    });

                    if (refCurrent) {
                        setActiveSection(refCurrent.id);
                    }
                }
            });
        };

        const observer = new IntersectionObserver(handleIntersection, options);

        Object.values(sectionRefs.current).forEach((ref) => {
            if (ref) {
                observer.observe(ref);
            }
        });

        return () => {
            observer.disconnect();
        };
    }, [sectionRefs, activeSection]);

    const handleLetterClick = (id) => {
        scrollTo(`#${id}`);
    };

    const grayLine = '<span class="text-blue text-4xl lg:text-6xl font-semibold lg:font-light">|</span>';

    return (
        <section
            className={`py-15 lg:pt-30 lg:pb-15 tabs-scrolling-section ${
                isMobile ? "overflow-hidden" : ""
            } ${sectionSpacingClassName} ${sectionColorMapping.bgColor}`}
            id={customId}
        >
            <div className="container">
                <div className="row mb-10 lg:mb-20">
                    <div className="col-12">
                        {title && <p className="tag-1 mb-5 uppercase">{title}</p>}
                        {subtitle && (
                            <h2
                                className={`${sectionColorMapping.primaryTextColor}`}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: renderSanitizedHtmlContent(subtitle.replace("|", grayLine)),
                                }}
                            />
                        )}
                        {body && <RichText data={data} className={`${styles.mainDescription} lg:body-big mt-5`} />}
                    </div>
                </div>
                <div className="row">
                    {isMobile ? (
                        <MobileTabsScrollingSection data={data} />
                    ) : (
                        <DesktopTabsScrollingSection
                            data={data}
                            sectionRefs={sectionRefs}
                            activeSection={activeSection}
                            handleLetterClick={handleLetterClick}
                        />
                    )}
                </div>
            </div>
        </section>
    );
};

export default TabsScrollingSection;
